import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV } from "./data";
import { DrezoroLogo } from "./DrezoroLogo";
import "./drezoro.css";

const Wordmark = ({ tagline = false, height = 28 }) => (
  <Link to="/drezoro" data-testid="dz-logo" aria-label="DREZORO home">
    <DrezoroLogo height={height} tagline={tagline} />
  </Link>
);

const FAVICONS = [
  ["dz-favicon-32", "/drezoro-favicon-32.png", "icon"],
  ["dz-favicon-16", "/drezoro-favicon-16.png", "icon"],
  ["dz-apple-touch", "/drezoro-apple-touch.png", "apple-touch-icon"],
];

function useDrezoroFavicon() {
  React.useEffect(() => {
    const originals = [...document.querySelectorAll('link[rel*="icon"]')];
    const saved = originals.map((l) => l.cloneNode());
    originals.forEach((l) => l.remove());
    const added = FAVICONS.map(([id, href, rel]) => {
      const link = document.createElement("link");
      link.id = id; link.rel = rel; link.href = href;
      document.head.appendChild(link);
      return link;
    });
    return () => {
      added.forEach((l) => l.remove());
      saved.forEach((l) => document.head.appendChild(l));
    };
  }, []);
}

export default function DrezoroLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useDrezoroFavicon();
  return (
    <div className="dz min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Wordmark height={30} />
          <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`dz-nav-${n.label.toLowerCase()}`}
                className={`text-sm transition-colors ${pathname === n.to ? "text-white" : "text-zinc-400 hover:text-white"}`}>
                {n.label}
              </Link>
            ))}
            <Link to="/drezoro/companies" data-testid="dz-nav-cta" className="dz-btn-secondary !py-2 !px-5 text-sm">Our Companies</Link>
          </nav>
          <button data-testid="dz-mobile-menu" onClick={() => setOpen(!open)} aria-label="Menu"
            className="md:hidden text-zinc-300 text-2xl leading-none">{open ? "×" : "≡"}</button>
        </div>
        {open && (
          <nav className="md:hidden border-t border-white/10 bg-black/90 px-6 py-4 space-y-3" aria-label="Mobile">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`dz-mnav-${n.label.toLowerCase()}`}
                className="block text-sm text-zinc-300 hover:text-white py-1.5">{n.label}</Link>
            ))}
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-white/10 pt-20 pb-8" data-testid="dz-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12"><Wordmark tagline height={34} /></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
            <div>
              <div className="dz-label mb-4">Company</div>
              <ul className="space-y-2.5 text-sm text-zinc-400">
                <li><Link to="/drezoro/about" className="hover:text-white" data-testid="dz-footer-about">About</Link></li>
                <li><Link to="/drezoro/companies" className="hover:text-white" data-testid="dz-footer-companies">Companies</Link></li>
                <li><Link to="/drezoro/technology" className="hover:text-white" data-testid="dz-footer-technology">Technology</Link></li>
              </ul>
            </div>
            <div>
              <div className="dz-label mb-4">Newsroom</div>
              <ul className="space-y-2.5 text-sm text-zinc-400">
                <li><Link to="/drezoro/news" className="hover:text-white" data-testid="dz-footer-news">News &amp; Press</Link></li>
                <li><span className="text-zinc-600" data-testid="dz-footer-careers">Careers <span className="text-[10px] uppercase tracking-wider">(Coming Soon)</span></span></li>
              </ul>
            </div>
            <div>
              <div className="dz-label mb-4">Legal</div>
              <ul className="space-y-2.5 text-sm text-zinc-400">
                <li><Link to="/drezoro/privacy" className="hover:text-white" data-testid="dz-footer-privacy">Privacy Policy</Link></li>
                <li><Link to="/drezoro/terms" className="hover:text-white" data-testid="dz-footer-terms">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <div className="dz-label mb-4">Contact</div>
              <ul className="space-y-2.5 text-sm text-zinc-400">
                <li><Link to="/drezoro/contact" className="hover:text-white" data-testid="dz-footer-contact">Contact Us</Link></li>
                <li><a href="mailto:info@drezoro.com" className="hover:text-white" data-testid="dz-footer-email">info@drezoro.com</a></li>
              </ul>
            </div>
          </div>
          <div aria-hidden="true" className="font-head font-semibold tracking-[0.18em] text-white/[0.05] text-[13vw] leading-none select-none whitespace-nowrap overflow-hidden">
            DREZORO
          </div>
          <div className="mt-6 pt-6 border-t border-white/5 text-xs text-zinc-600 flex flex-col sm:flex-row justify-between gap-2">
            <span>Copyright © {new Date().getFullYear()} DREZORO. All rights reserved.</span>
            <span>The parent technology company behind Vozelto, TurnKey, Black Ember Coffee, and Operation Enough.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
